/**
 * Permission and delivery are two facts, and one value cannot carry both.
 *
 * This model exists because "granted" was used for both "the browser allowed
 * notifications" and "we can actually deliver to this device". Every screen
 * that consumed it then had to guess which one it meant, and each round of
 * fixes corrected one more place where the two diverge: a refusal that turned
 * green on retry, then on refocus, then a transient failure that reported
 * success anyway. The branches were symptoms; this is the contract.
 *
 * Four outcomes, each true of exactly one situation:
 *
 * - `registered`  — permission granted AND a destination is stored. The only
 *                   one that means a notification can arrive.
 * - `denied`      — the OS or browser refused permission. Settings can fix it.
 * - `unavailable` — permission granted, but registration did not complete for a
 *                   reason that may resolve on its own: offline, the session
 *                   not ready yet, no VAPID key, an API build without the
 *                   route, a runtime with no PushManager or native module.
 *                   NOT a refusal — see below.
 * - `rejected`    — permission granted, and the server permanently refused the
 *                   subscription (HTTP 400). Retrying sends the same payload to
 *                   the same answer.
 *
 * `unavailable` is deliberately distinct from `denied`. Collapsing them is a
 * mistake this codebase has already made once: reporting a failed token upload
 * as a refusal made the very first Allow look like a denial and pushed the user
 * into a pointless try-again / open-settings loop (see `syncExpoPushToken`).
 * Keeping it apart is what lets the UI stay quiet about something that is
 * probably about to fix itself, without ever claiming delivery works.
 *
 * ## Scope: `unavailable` and `rejected` are produced by the WEB path only
 *
 * This model exists for web push, where a browser's subscription is stored via
 * an API call that can be refused. **Native reports only `registered` or
 * `denied`, exactly as it always has**: its permission answer is the outcome,
 * and the Expo token upload behind it is best-effort with a foreground retry
 * (`syncExpoPushToken`). So on native `registered` means "permission granted",
 * not "a destination is confirmed stored" — stated here rather than left for
 * someone to discover, because the whole point of this type is that a value
 * means one thing.
 *
 * That asymmetry is deliberate and is NOT a gap to be closed opportunistically.
 * Making native's upload failure visible changes long-standing, device-tested
 * behaviour and needs its own native task and device pass; folding it into web
 * work once already introduced a false `registered` on native (a token that
 * failed to upload still reported success) — the exact bug this type exists to
 * prevent.
 */
export type PushSetupOutcome =
  | "registered"
  | "denied"
  | "unavailable"
  | "rejected";

/**
 * The API refused to store this browser's subscription (HTTP 400).
 *
 * Deliberately NOT named after the endpoint allowlist. That route returns 400
 * for the allowlist, but equally for malformed keys, a missing field, an
 * over-long value or an unknown one — so the only thing the client can honestly
 * conclude is "the server rejected this, and sending it again will not change
 * that". Naming it after one cause would make a browser/API contract regression
 * look like a provider-host problem. `detail` carries the server's own message
 * for the console; it never contains the endpoint.
 */
export class PushRegistrationRejectedError extends Error {
  // A plain field, not a constructor parameter property: the mobile tests run
  // under Node's strip-only TypeScript, which cannot compile the latter.
  readonly detail?: string;

  constructor(detail?: string) {
    super("The API rejected this browser's push subscription.");
    this.name = "PushRegistrationRejectedError";
    this.detail = detail;
  }
}

/** What the onboarding notification card is showing. */
export type NotificationCardState = "idle" | "granted" | "denied" | "blocked";

/**
 * What the card may claim, given what actually happened.
 *
 * Every writer goes through this. The card used to be set from the browser
 * permission alone in two other places — the "Try again" press and the
 * focus/foreground sync — so a refused browser went back to showing "Allowed"
 * on the next tap or refocus, and (with location ready) could auto-complete
 * onboarding.
 *
 * The invariant: **only `registered` turns the card green.** Nothing else does,
 * including a granted permission whose registration never completed.
 */
export function notificationCardState(args: {
  /** The card's current state. */
  previous: NotificationCardState;
  /** What the registration attempt achieved. */
  outcome: PushSetupOutcome;
  /** Whether the OS will still show a prompt. Only consulted for a refusal. */
  canAskAgain: boolean;
  /**
   * True for the focus/foreground sync. A passive check must not wake an
   * untouched card into accusing the user of a refusal they never made.
   */
  passive: boolean;
}): NotificationCardState {
  // The only proof of delivery.
  if (args.outcome === "registered") return "granted";

  // Probably temporary, and the caller did not ask. Say nothing rather than
  // downgrade a card that may be green for good reason; an explicit press
  // still gets an honest "Try again".
  if (args.outcome === "unavailable") {
    return args.passive ? args.previous : "denied";
  }

  // A passive check must not accuse an untouched card.
  if (args.passive && args.previous === "idle") return "idle";

  // A refused registration is not an OS refusal, so never "blocked": that
  // offers Open Settings for settings which are already correct.
  if (args.outcome === "rejected") return "denied";

  return args.canAskAgain ? "denied" : "blocked";
}
